import { useEffect, useLayoutEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Upload, Plus, Settings2,
  Download, AlertTriangle,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PartnerSignupLinkCard from "@/components/PartnerSignupLinkCard";
import { StageColumn } from "@/components/kanban/StageColumn";
import { ImportModal } from "@/components/kanban/ImportModal";
import { ClientModal } from "@/components/kanban/ClientModal";
import { MoveConfirmModal } from "@/components/kanban/MoveConfirmModal";
import { StageEditor } from "@/components/kanban/StageEditor";
import { ClientDrawer } from "@/components/kanban/ClientDrawer";
import { downloadChaseList } from "@/components/kanban/utils";

// Re-export ClientDrawer at the legacy import path so existing consumers
// (``components/CustomersList.jsx``) keep working without touching every
// import site. New code should import from ``@/components/kanban/ClientDrawer``.
export { ClientDrawer };

// The refactor split (v1.10.99): the 2400-line monolith is now
// composed from ``components/kanban/*``. This file keeps only the
// board-level orchestration — scroll container CSS (load-bearing for
// sticky column headers), drag/drop routing, drawer open/close, and
// modal wiring. See each sub-component for its own contract.

export default function MigrationBoard({ isPartnerView = false }) {
  const { partnerCode: pcParam } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const partnerCode = isPartnerView ? state?.user?.partner_code : pcParam;
  const [board, setBoard] = useState(null);
  const [clients, setClients] = useState([]);
  const [openClient, setOpenClient] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showStages, setShowStages] = useState(false);

  const endpoint = isPartnerView
    ? "/partner/migration"
    : `/commissions/migrations/${partnerCode}`;

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get(endpoint);
      setBoard(data.board);
      setClients(data.clients || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load board");
    }
  }, [endpoint]);
  useEffect(() => { if (partnerCode || isPartnerView) reload(); }, [partnerCode, isPartnerView, reload]);

  // Deep-link auto-open — the notification bell navigates here with
  // ?open=<client_id>. Match against loaded clients, pop the drawer,
  // strip the param. Toast if the id isn't on this board (usually
  // means the customer belongs to a different partner).
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || clients.length === 0) return;
    const match = clients.find((c) => c.id === openId);
    if (match) {
      setOpenClient(match);
    } else {
      toast.info("That customer isn't on this board — try switching partner.");
    }
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [clients, searchParams, setSearchParams]);

  // Per-card "seen" — when the drawer opens for a card with unread
  // activity, ping the mark-seen endpoint + optimistically clear the
  // pulse locally so the badge stops instantly.
  useEffect(() => {
    if (!openClient?.id || !openClient.has_unread_activity) return;
    const cid = openClient.id;
    const seenEndpoint = isPartnerView
      ? `/partner/migration/clients/${cid}/mark-seen`
      : `/commissions/migrations/clients/${cid}/mark-seen`;
    setClients((prev) => prev.map((c) =>
      c.id === cid ? { ...c, has_unread_activity: false } : c));
    setOpenClient((c) => c && c.id === cid ? { ...c, has_unread_activity: false } : c);
    api.post(seenEndpoint).catch(() => {
      // Best-effort — failure just means the pulse returns on next reload.
    });
  }, [openClient?.id, openClient?.has_unread_activity, isPartnerView]);

  const clientsByStage = useMemo(() => {
    const grouped = {};
    const stageKeys = new Set((board?.stages || []).map(s => s.key));
    (board?.stages || []).forEach(s => { grouped[s.key] = []; });
    const orphans = [];
    (clients || []).forEach(c => {
      if (stageKeys.has(c.stage)) {
        grouped[c.stage].push(c);
      } else {
        orphans.push(c);
      }
    });
    return { grouped, orphans };
  }, [clients, board]);

  // Dynamic board height — measure the container's distance from the
  // viewport top and fill the remaining space. Handles env banner,
  // wrapped header rows, mobile chrome without a hard-coded calc().
  const scrollRef = useRef(null);
  // eslint-disable-next-line no-unused-vars
  const [scrollMaxH, setScrollMaxH] = useState(600);
  useLayoutEffect(() => {
    function compute() {
      if (!scrollRef.current) return;
      const top = scrollRef.current.getBoundingClientRect().top;
      const h = Math.max(320, window.innerHeight - top - 24);
      setScrollMaxH(h);
    }
    compute();
    window.addEventListener("resize", compute);
    const raf = requestAnimationFrame(compute);
    return () => {
      window.removeEventListener("resize", compute);
      cancelAnimationFrame(raf);
    };
  }, [board]);

  async function moveStage(client, stageKey) {
    try {
      const url = isPartnerView
        ? `/partner/migration/clients/${client.id}`
        : `/commissions/migrations/clients/${client.id}`;
      const { data } = await api.patch(url, { stage: stageKey });
      setClients(prev => prev.map(c => c.id === client.id ? data : c));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Move failed");
    }
  }

  // Drag & drop stage moves — confirmation-gated so an accidental drop
  // doesn't silently reshuffle the board. Pre-flights the Installed +
  // Live moves so partners see the missing-creds toast before dropping.
  const [pendingMove, setPendingMove] = useState(null);
  function onDropOnStage(clientId, toStageKey) {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    if (client.stage === toStageKey) return;
    if (toStageKey === "installed" || toStageKey === "live") {
      const missing = [];
      if (!client.nbn_username) missing.push("NBN username");
      if (!client.nbn_password) missing.push("NBN password");
      if (!client.avc_number)   missing.push("AVC number");
      if (missing.length) {
        const stagePretty = toStageKey === "installed" ? "Installed" : "Live";
        toast.error(
          `Missing ${missing.join(", ")} — please fill in the full NBN `
          + `credentials block before moving to ${stagePretty}.`
        );
        setOpenClient(client);
        return;
      }
    }
    setPendingMove({ client, from: client.stage, to: toStageKey });
  }
  async function confirmMove() {
    if (!pendingMove) return;
    await moveStage(pendingMove.client, pendingMove.to);
    setPendingMove(null);
  }
  function cancelMove() { setPendingMove(null); }

  if (!board) return <div className="p-6 text-slate-500">Loading…</div>;

  const syncroUrlBase = !isPartnerView && board.syncro_subdomain
    ? `https://${board.syncro_subdomain}.syncromsp.com` : null;

  return (
    <div className="space-y-6" data-testid="migration-board">
      <div>
        {!isPartnerView && (
          <Link to="/admin/onboarding" className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 mb-3">
            <ArrowLeft className="w-3 h-3" /> All onboarding boards
          </Link>
        )}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold">
              {board.partner_code} onboarding board
            </div>
            <h1 className="font-display text-3xl tracking-tight mt-1">{board.name}</h1>
          </div>
          <div className="flex flex-wrap gap-2 mb-4">
            <button
              data-testid="board-import-csv"
              onClick={() => setShowImport(true)}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm"
            >
              <Upload className="w-4 h-4" /> Import CSV
            </button>
            {!isPartnerView && (
              <button
                data-testid="board-chase-list"
                onClick={() => downloadChaseList(partnerCode)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm"
                title="Download printable outreach list of clients still in not-contacted / contacted / info-sent"
              >
                <Download className="w-4 h-4" /> Chase list
              </button>
            )}
            <button
              data-testid="board-add-client"
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-white px-3 py-2 rounded-lg text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add client
            </button>
            {!isPartnerView && (
              <button
                data-testid="board-configure-stages"
                onClick={() => setShowStages(true)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-800 hover:bg-slate-800 text-sm"
              >
                <Settings2 className="w-4 h-4" /> Stages
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Public signup URLs — pinned atop every board so admins &
          partners don't have to hunt for the share-these-with-customers
          links. transferUpstream falls back to a "to {partner}" label
          when the upstream RSP isn't configured on the partner. */}
      <PartnerSignupLinkCard
        partnerCode={board.partner_code}
        partnerName={board.name}
        transferUpstream={board.transfer_upstream_name}
      />

      {/* Orphan band — clients whose ``stage`` doesn't match any column
          on this board (usually an importer default drifting from the
          board's stage list). Clicking a chip opens the drawer so the
          operator can move it to a real stage. */}
      {clientsByStage.orphans.length > 0 && (
        <div
          className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2"
          data-testid="kanban-orphans"
        >
          <div className="flex items-center gap-2 text-amber-200 text-xs font-semibold uppercase tracking-wider">
            <AlertTriangle className="w-3.5 h-3.5" />
            {clientsByStage.orphans.length} customer{clientsByStage.orphans.length === 1 ? "" : "s"} not on the board
            <span className="text-amber-200/70 font-normal normal-case tracking-normal">
              — their stage doesn&apos;t match any column. Click to move to a real stage.
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {clientsByStage.orphans.map(c => (
              <button
                key={c.id}
                data-testid={`orphan-chip-${c.id}`}
                onClick={() => setOpenClient(c)}
                className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-amber-500/20 hover:bg-amber-500/30 text-amber-100 text-xs font-medium transition-colors"
                title={`Stage on record: ${c.stage}`}
              >
                <span className="truncate max-w-[180px]">
                  {c.business_name || c.contact_name || c.email || "(no name)"}
                </span>
                <span className="font-mono text-[10px] opacity-70">stage: {c.stage}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Kanban scroll container.
          LOAD-BEARING CSS — DO NOT MODIFY without testing sticky headers
          against a 60-item column on a 10-item column mix. Sticky column
          headers (see StageColumn) need overflow-y to stay ``visible``
          so they can escape into the natural page scroll context. Any
          ``overflow-y: auto|scroll|hidden`` on this box would create a
          scroll container that traps sticky positioning. Horizontal
          overflow uses ``clip`` (not ``auto``) — clip prevents visual
          bleed WITHOUT establishing a scroll container. Combined with
          ``items-stretch`` on the inner flex row + each column's
          ``min-h-[calc(100vh-12rem)]``, this keeps every sticky header
          in unison as the user scrolls the tallest column. */}
      <div
        ref={scrollRef}
        className="rounded-xl border border-slate-800/60 bg-slate-950/20 p-3 pb-8"
        style={{ overflowX: "clip" }}
        data-testid="kanban-scroll"
      >
        <div className="flex gap-4 min-w-max items-stretch">
          {(board.stages || []).map(stage => (
            <StageColumn
              key={stage.key}
              stage={stage}
              clients={clientsByStage.grouped[stage.key] || []}
              onOpen={(c) => setOpenClient(c)}
              onDropClient={onDropOnStage}
              isPartnerView={isPartnerView}
              syncroUrlBase={syncroUrlBase}
            />
          ))}
        </div>
      </div>

      {showImport && !isPartnerView && (
        <ImportModal partnerCode={partnerCode} onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); reload(); }} />
      )}
      {pendingMove && (
        <MoveConfirmModal
          pending={pendingMove}
          stages={board.stages}
          onConfirm={confirmMove}
          onCancel={cancelMove}
        />
      )}
      {showCreate && (
        <ClientModal
          board={board}
          isPartnerView={isPartnerView}
          onClose={() => setShowCreate(false)}
          onSaved={() => { setShowCreate(false); reload(); }}
        />
      )}
      {openClient && (
        <ClientDrawer
          client={openClient}
          board={board}
          isPartnerView={isPartnerView}
          onClose={() => setOpenClient(null)}
          onChanged={(next) => {
            if (next === "deleted") {
              setClients(prev => prev.filter(c => c.id !== openClient.id));
              setOpenClient(null);
            } else if (next) {
              setClients(prev => prev.map(c => c.id === next.id ? next : c));
              setOpenClient(next);
            }
          }}
        />
      )}
      {showStages && !isPartnerView && (
        <StageEditor
          board={board}
          onClose={() => setShowStages(false)}
          onSaved={(b) => { setBoard(b); setShowStages(false); }}
          partnerCode={partnerCode}
        />
      )}
    </div>
  );
}
